import React from "react";
import { HISTORICAL } from "../../mock";
import { Award, Sparkles } from "lucide-react";
import ClassPhotoMemories from "./ClassPhotoMemories";

const isSchoolPhoto = (p) => p.category === "Education";

// Subtle rotation per item to create a "scrapbook" feel
const ROTATIONS = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "-rotate-1", "rotate-2", "-rotate-1", "rotate-1", "-rotate-2", "rotate-1"];

// Repeating layout pattern (per row of 5)
const SPANS = [
  "col-span-12 md:col-span-7",
  "col-span-12 md:col-span-5",
  "col-span-12 sm:col-span-6 md:col-span-4",
  "col-span-12 sm:col-span-6 md:col-span-4",
  "col-span-12 md:col-span-4"
];

// Vibrant per-category color coding
const CAT_COLORS = {
  "Honors":      "bg-saffron text-emerald-deep",
  "Royal":       "bg-plum text-white",
  "Public Life": "bg-teal text-white",
  "Service":     "bg-coral text-white",
  "Sports":      "bg-indigoVivid text-white",
  "Author":      "bg-emerald-deep text-champagne",
  "Family":      "bg-coral-soft text-coral",
  "Press":       "bg-indigo-soft text-indigoVivid",
  "Portrait":    "bg-teal-soft text-teal",
  "Education":   "bg-emerald-soft text-emerald-deep"
};

export default function Honors() {
  return (
    <section id="honors" className="scroll-smooth-section py-28 relative overflow-hidden">
      {/* Decorative cream backdrop with patterned image hints */}
      <div className="absolute inset-0 bg-cream" />
      <div className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, rgba(245,158,11,0.22) 0, transparent 30%), radial-gradient(circle at 85% 75%, rgba(13,148,136,0.18) 0, transparent 35%)"
        }}
      />
      {/* Colorful decorative blobs */}
      <div className="blob-saffron w-[400px] h-[400px] -top-20 right-10" />
      <div className="blob-teal w-[380px] h-[380px] bottom-1/3 -left-20" />
      <div className="blob-coral w-[260px] h-[260px] bottom-20 right-1/4 opacity-25" />
      {/* Faint photo collage backdrop */}
      <div className="image-backdrop" style={{ backgroundImage: `url(${HISTORICAL[0].src})` }} />
      <div className="image-backdrop-overlay" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative z-10">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-gold" />
            <span className="text-[11px] tracking-[0.35em] uppercase text-gold font-medium">Honors & Public Life</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-navy leading-[1.05] font-medium">
            A life lived <span className="italic text-gold">in service of others</span>
          </h2>
          <p className="mt-5 text-stone-700 text-lg max-w-2xl">
            Glimpses from a public life — felicitations, awards, and audiences with luminaries of Kerala. These archival photographs
            have been digitally restored to honour the moments they captured.
          </p>
        </div>

        {/* Editorial scrapbook grid */}
        <div className="mt-16 grid grid-cols-12 gap-6 md:gap-8">
          {HISTORICAL.map((p, i) => {
            if (isSchoolPhoto(p)) {
              return (
                <ClassPhotoMemories
                  key={i}
                  photoKey={p.photoKey || `education-${i}`}
                  src={p.src}
                  title={p.title}
                  caption={p.caption}
                  year={p.year}
                  category={p.category}
                />
              );
            }
            const span = SPANS[i % SPANS.length];
            const rot = ROTATIONS[i % ROTATIONS.length];
            return (
              <figure key={i} className={`${span} ${rot} polaroid relative gold-corner`}>
                <div className="photo-area aspect-[4/3]">
                  <img
                    src={p.src}
                    alt={p.title}
                    className="w-full h-full object-cover photo-enhance"
                  />
                </div>
                <figcaption className="pt-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 ${CAT_COLORS[p.category] || "bg-saffron text-emerald-deep"} text-[10px] uppercase tracking-[0.25em] font-bold px-2.5 py-1 rounded-full`}>
                      <Sparkles className="w-3 h-3" />
                      {p.category}
                    </span>
                    <span className="ml-auto text-stone-500 text-[11px] tracking-tight italic">{p.year}</span>
                  </div>
                  <div className="font-display text-navy text-xl mt-3 leading-tight">{p.title}</div>
                  <div className="font-serif-accent italic text-stone-600 text-sm mt-1">{p.caption}</div>
                </figcaption>
              </figure>
            );
          })}
        </div>

        {/* Call to action card — colorful per card */}
        <div className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            { num: "42+", label: "Awards Received", text: "Across rural health, education, charity, and literature.", bg: "bg-gradient-to-br from-emerald-deep to-teal", accent: "text-champagne" },
            { num: "28+", label: "Honorary Roles", text: "Trustee, Patron and Chairman positions across Kerala.", bg: "bg-gradient-to-br from-coral to-saffron", accent: "text-white" },
            { num: "30+", label: "Years of Service", text: "Three decades teaching and treating in Kerala's medical colleges.", bg: "bg-gradient-to-br from-indigoVivid to-plum", accent: "text-champagne" }
          ].map((s, i) => (
            <div key={i} className={`${s.bg} text-white rounded-3xl p-7 lift relative overflow-hidden`}>
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-12 -left-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
              <Award className={`w-8 h-8 ${s.accent} relative`} />
              <div className={`font-display text-6xl ${s.accent} mt-3 relative leading-none`}>{s.num}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] mt-2 relative font-semibold">{s.label}</div>
              <p className="text-white/85 text-sm mt-4 leading-relaxed relative">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
